import { mat3 } from 'gl-matrix';
import type { PlaneView } from './views/PlaneView';

/**
 * A view's part in a {@link ViewGeometryLink}. A `lead` view keeps its own
 * geometry and hands it to the `follow` views facing its way; a `follow`
 * view shows what its lead shows, at the lead's scale.
 */
export type GeometryLinkRole = 'lead' | 'follow';

/**
 * Keeps the views of one Environment that face the same way at one screen
 * scale, in mm per pixel, and centred on one point of their plane.
 *
 * A follow view takes its lead's geometry whenever either changes, so a
 * resize, a fit or a zoom never leaves the two at different scales. A pan
 * or zoom the user makes in a follow view is carried to its lead and to
 * the lead's other follow views. Two leads never move each other, and a
 * follow view with no lead facing its way keeps its own geometry.
 */
export class ViewGeometryLink {
    private readonly roles = new Map<PlaneView, GeometryLinkRole>();
    private syncing = false;

    public roleOf(view: PlaneView): GeometryLinkRole | undefined {
        return this.roles.get(view);
    }

    /** Gives `view` its part, or takes it out of the link for undefined. */
    public setRole(view: PlaneView, role: GeometryLinkRole | undefined): void {
        if (role === undefined) {
            this.roles.delete(view);
            if (view.geometryLink === this) {
                view.geometryLink = undefined;
            }
            return;
        }
        this.roles.set(view, role);
        view.geometryLink = this;
        this.viewChanged(view, false);
    }

    /**
     * Carries a change of `view`'s geometry across the link. `byUser` marks
     * a pan or zoom the user made in that view, as opposed to a resize, a
     * fit or a zoom every view takes.
     */
    public viewChanged(view: PlaneView, byUser: boolean): void {
        const role = this.roles.get(view);
        if (role === undefined || this.syncing) {
            return;
        }
        const lead = role === 'lead' ? view : this.leadFacing(view);
        if (!lead) {
            return;
        }
        this.syncing = true;
        try {
            if (role === 'follow' && !byUser) {
                view.takeGeometryOf(lead);
                return;
            }
            for (const peer of [lead, ...this.followersOf(lead)]) {
                if (peer !== view) {
                    peer.takeGeometryOf(view);
                }
            }
        } finally {
            this.syncing = false;
        }
    }

    private leadFacing(view: PlaneView): PlaneView | undefined {
        for (const [peer, role] of this.roles) {
            if (role === 'lead' && facesSameWay(peer, view)) {
                return peer;
            }
        }
        return undefined;
    }

    private followersOf(lead: PlaneView): PlaneView[] {
        return [...this.roles]
            .filter(
                ([peer, role]) =>
                    role === 'follow' && this.leadFacing(peer) === lead
            )
            .map(([peer]) => peer);
    }
}

function facesSameWay(a: PlaneView, b: PlaneView): boolean {
    return mat3.equals(a.rotation, b.rotation);
}
